import { useThemeColors } from '@/hooks/useThemeColors';
import { CheckCircle, Play } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface ClassData {
    id: number;
    title: string;
    description?: string;
    image?: string;
    order: number;
    is_completed?: boolean;
    topic: {
        id: number;
        title: string;
    };
}

interface ClassCardProps {
    classData: ClassData;
    classNumber: number;
    onClick?: () => void;
}

const ClassCard: React.FC<ClassCardProps> = ({ classData, classNumber, onClick }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const isCompleted = classData.is_completed || false;

    return (
        <div
            className={`group relative cursor-pointer overflow-hidden rounded-xl border transition-all duration-200 hover:border-gray-300 ${
                isCompleted
                    ? 'border-green-200 bg-green-50'
                    : 'border-gray-200 bg-white'
            }`}
            onClick={onClick}
        >
            {/* Subtle top accent */}
            <div
                className={`h-1 ${isCompleted ? 'bg-green-500' : colors.primaryBg}`}
            ></div>

            <div className="p-6">
                {/* Class number badge */}
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                isCompleted ? 'bg-green-500' : colors.primaryBg
                            }`}
                        >
                            {isCompleted ? (
                                <CheckCircle size={16} className="text-white" />
                            ) : (
                                <Play
                                    size={14}
                                    className="text-white"
                                    fill="currentColor"
                                />
                            )}
                        </div>
                        <span
                            className={`text-sm font-semibold ${isCompleted ? 'text-green-700' : colors.iconColor}`}
                        >
                            {t('course.class')} {classNumber}
                        </span>
                    </div>
                    {isCompleted && (
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-600">
                            {t('course.completed')}
                        </span>
                    )}
                </div>

                {/* Title */}
                <h3
                    className={`text-sm leading-snug font-semibold ${isCompleted ? 'text-green-800' : 'text-gray-900'}`}
                >
                    {classData.title}
                </h3>
            </div>
        </div>
    );
};

export default ClassCard;
